import LoadError from "../components/LoadError";
import useActionLock from "../hooks/useActionLock";
import { formatMoney } from "../utils/format";
import { useConfirm } from "../components/ConfirmProvider";
import {
  Building,
  Edit,
  Mail,
  MapPin,
  Phone,
  Plus,
  CheckSquare,
  Archive,
  RotateCcw,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ClientForm from "../components/ClientForm";
import { clientsAPI } from "../services/api";
import toast from "react-hot-toast";

const Clients = () => {
  const confirm = useConfirm();
  const [actionBusy, runAction] = useActionLock();
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("actifs");
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadError("");
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      setLoadError("Impossible de charger les clients. Les données existantes sont conservées.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = (clientData) => runAction(async () => {
    try {
      await clientsAPI.create(clientData);
      toast.success("Client créé avec succès");
      closeForm();
      await loadClients();
    } catch (error) {
      toast.error(typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "Erreur lors de la création du client");
      throw error;
    }
 });

  const handleUpdateClient = (clientData) => runAction(async () => {
    try {
      await clientsAPI.update(editingClient.id, clientData);
      toast.success("Client mis à jour avec succès");
      closeForm();
      await loadClients();
    } catch (error) {
      toast.error(typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "Erreur lors de la mise à jour du client");
      throw error;
    }
 });

  const handleArchiveClient = (client) => runAction(async () => {
    const restore = client.archived;
    if (!await confirm({ title: restore ? "Restaurer le client ?" : "Archiver le client ?", confirmLabel: restore ? "Restaurer" : "Archiver", details: [["Client", client.nom]], description: restore ? "Le client sera proposé pour de nouvelles créations. Ses abonnements resteront en pause." : "La fiche et tous les documents sont conservés. Les abonnements de ce client seront mis en pause. Les relances de ses factures existantes restent inchangées." })) return;
    try { await clientsAPI[restore ? "restore" : "archive"](client.id); await loadClients(); toast.success(restore ? "Client restauré" : "Client archivé"); }
    catch (error) { toast.error(error.response?.data?.detail || "Impossible de modifier l'archivage"); }
  });

  const openEditForm = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingClient(null);
  };

  const filteredClients = clients.filter((client) => (filter === "tous" || Boolean(client.archived) === (filter === "archives")) && `${client.nom || ""} ${client.societe || ""} ${client.email || ""}`.toLocaleLowerCase("fr").includes(search.trim().toLocaleLowerCase("fr")));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des clients
          </h1>
          <p className="text-gray-600">
            Gérez vos clients et leurs informations
          </p>
        </div>
        <button
          disabled={actionBusy} onClick={() => { setEditingClient(null); setShowForm(true); }}
          className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Nouveau client</span>
        </button>
      </div>

      <label className="block text-sm">Rechercher un client<input className="input-field mt-1 max-w-lg" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nom, société ou email" /></label>
      <div className="flex flex-wrap gap-2">{[["actifs", "Actifs"], ["archives", "Archivés"], ["tous", "Tous"]].map(([key, label]) => <button type="button" key={key} className="btn-secondary" aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>)}</div>
      <LoadError message={loadError} onRetry={loadClients} />
      <p role="status" className="text-sm text-gray-500">{filteredClients.length} résultat(s)</p>
      {filteredClients.length === 0 && !loadError && clients.length > 0 && <div className="card"><p>Aucun client ne correspond à cette recherche.</p><button className="btn-secondary mt-3" onClick={() => { setSearch(""); setFilter("actifs"); }}>Réinitialiser les filtres</button></div>}
      {/* Liste des clients */}
      <div className="card">
        {clients.length === 0 && !loadError ? (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun client
            </h3>
            <p className="text-gray-600 mb-4">
              Ajoutez votre premier client pour commencer
            </p>
            <button disabled={actionBusy} onClick={() => { setEditingClient(null); setShowForm(true); }} className="btn-primary">
              Ajouter un client
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <div
                key={client.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-gray-900">
                        <Link className="hover:underline text-primary-700" to={`/clients/${client.id}`}>{client.nom}</Link>
                      </h3>
                      {client.archived && <span className="text-xs text-gray-500">Archivé</span>}
                      {client.societe && (
                        <p className="text-sm text-gray-600 flex items-center">
                          <Building className="h-4 w-4 mr-1" />
                          {client.societe}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/taches?client_id=${client.id}`)}
                      className="text-primary-600 hover:text-primary-900 p-1"
                      title="Voir les tâches"
                    >
                      <CheckSquare className="h-4 w-4" />
                    </button>
                    <button
                      disabled={actionBusy || client.archived} onClick={() => openEditForm(client)}
                      className="text-blue-600 hover:text-blue-900 p-2 min-h-[44px] min-w-[44px]"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      disabled={actionBusy} onClick={() => handleArchiveClient(client)}
                      className="text-red-600 hover:text-red-900 p-2 min-h-[44px] min-w-[44px]"
                      title={client.archived ? "Restaurer" : "Archiver"}
                    >
                      {client.archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span>
                      {client.adresse}, {client.ville} {client.code_postal}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">{formatMoney(client.taux_horaire, client.devise)} / heure · Paiement à {client.delai_paiement} jours</p>
                  <div className="flex items-start">
                    <Mail className="h-4 w-4 mr-2" />
                    <span className="break-all">{client.email}</span>
                  </div>
                  {client.telephone && (
                    <div className="flex items-start">
                      <Phone className="h-4 w-4 mr-2" />
                      <span>{client.telephone}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                      {client.devise}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulaire client */}
      {showForm && (
        <ClientForm
          client={editingClient}
          onSubmit={editingClient ? handleUpdateClient : handleCreateClient}
          onCancel={closeForm}
        />
      )}
    </div>
  );
};

export default Clients;
